import React, { useEffect } from 'react';
import { IconType } from 'react-icons';
import { MdCheckCircle, MdClear, MdInfo, MdWarning } from 'react-icons/md';
// eslint-disable-next-line
import Alert from '@reach/alert';
import classNames from 'classnames';

import { Icon } from '../icon';

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

export type ToastProps = {
  variant: ToastVariant;
  isClosable?: boolean;
  autoRemove?: boolean;
  duration?: number;
  message?: React.ReactNode;
  description?: React.ReactNode;
  icon?: IconType;
  iconClassName?: string;
  className?: string;
  onClose: () => void;
};

export const Toast = ({
  message,
  description,
  className,
  variant,
  isClosable = true,
  autoRemove = true,
  duration = 5000,
  icon,
  iconClassName,
  onClose,
}: ToastProps) => {
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (autoRemove) {
      timeout = setTimeout(() => {
        onClose();
      }, duration);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [autoRemove, duration, onClose]);

  return (
    <Alert
      className={classNames(
        'relative px-4 py-3 rounded-lg text-sm',
        {
          'pr-12': isClosable,
        },
        {
          'bg-gray-100 text-gray-700': variant === 'info',
          'bg-emerald-200 text-emerald-800': variant === 'success',
          'bg-orange-200 text-orange-800': variant === 'warning',
          'bg-red-200 text-red-800': variant === 'danger',
        },
        className
      )}
    >
      <div className="flex items-center space-x-2">
        {icon && (
          <Icon
            className={classNames('flex-none', iconClassName)}
            icon={
              {
                info: <MdInfo className={className} />,
                success: <MdCheckCircle className={className} />,
                warning: <MdWarning className={className} />,
                danger: <MdWarning className={className} />,
              }[variant]
            }
          />
        )}
        <div className="space-y-1">
          <div className="font-medium">{message}</div>
          {description && <div>{description}</div>}
        </div>
      </div>
      {isClosable && (
        <button
          type="button"
          aria-label="Close"
          className={classNames(
            'absolute top-2 right-2 p-1 rounded-lg',
            'hover:text-white focus:outline-none focus:ring focus:ring-opacity-50',
            {
              'hover:bg-gray-700 focus:ring-gray-700': variant === 'info',
              'hover:bg-emerald-800 focus:ring-emerald-800':
                variant === 'success',
              'hover:bg-orange-800 focus:ring-orange-800':
                variant === 'warning',
              'hover:bg-red-800 focus:ring-red-800': variant === 'danger',
            }
          )}
          onClick={onClose}
        >
          <Icon icon={<MdClear />} />
        </button>
      )}
    </Alert>
  );
};
